import React, { useMemo } from 'react';
import { Col, Row } from 'react-bootstrap';
import { IExternalPost } from '../../dto/IDetailModalData';
import { CustomImageHandler } from '../../../../../../../shared/components/CustomImageHandler';
import { formatDate } from '../../../../../../../shared/functions/Functions';
import { numberFormatter } from '../../../../../../../utils/helpers/numberFormater';
import { FaCommentDots, FaHeart } from 'react-icons/fa';
import TooltipOverlay from '../../../../../../../shared/components/tooltip/Tooltip';

interface PopularPostsGalleryProps {
    externalPosts?: IExternalPost[];
    isSponsored?: boolean;
}

export const ExternalPosts: React.FC<PopularPostsGalleryProps> = ({
                                                                      externalPosts,
                                                                      isSponsored = false,
                                                                  }) => {
    const posts = useMemo(
      () => Object.values(externalPosts || {}),
      [externalPosts]
    );

    const renderSponsors = (item: IExternalPost) => {
        if (!isSponsored) return null;

        const sponsors = item?.sponsors || [];

        if (sponsors.length <= 1) {
            return (
              <>
                  <p className="text-muted d-block mb-1">
                      Collaborated with
                  </p>

                  {sponsors.length === 1 ? (
                    sponsors.map((sponsor) => (
                      <div
                        key={sponsor.user_id}
                        className="d-flex align-items-center gap-2"
                      >
                          <CustomImageHandler
                            altTag="Brand image"
                            classes="user-img-rounded"
                            thumbnailPath={sponsor.logo_url ?? ''}
                            photoPath={sponsor.logo_url ?? ''}
                          />
                          <p className="mb-0">{sponsor.name}</p>
                      </div>
                    ))
                  ) : (
                    <div className="d-flex align-items-center">
                        <CustomImageHandler
                          altTag="Brand image"
                          classes="user-img-rounded"
                          thumbnailPath=""
                          photoPath=""
                        />
                        <p className="mb-0">No data</p>
                    </div>
                  )}
              </>
            );
        }

        // Multiple sponsors
        return (
          <>
              <small className="text-muted d-block">
                  Collaborated with
              </small>

              <div className="d-flex align-items-center">
                  {sponsors.map((sponsor, index) => (
                    <div
                      key={sponsor.user_id}
                      style={{
                          marginLeft: index === 0 ? 0 : -10,
                          zIndex: sponsors.length - index,
                      }}
                    >
                        <TooltipOverlay content={sponsor.name}>
                            <CustomImageHandler
                              altTag="Sponsor logo"
                              classes="user-img-rounded border"
                              thumbnailPath={sponsor.logo_url ?? ''}
                              photoPath={sponsor.logo_url ?? ''}
                            />
                        </TooltipOverlay>
                    </div>
                  ))}
              </div>
          </>
        );
    };

    return (
      <Row>
          {posts.map((item) => (
            <Col key={item?.id} sm={4} lg={3}>
                <div className="border rounded p-2 mb-4">

                    {renderSponsors(item)}

                    {/* Post */}
                    <a
                      href={item?.url ?? ''}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                        <div className="position-relative">
                            <CustomImageHandler
                              altTag="Social platforms feed images"
                              classes="feed-image box-shadow"
                              thumbnailPath={item?.thumbnail ?? ''}
                              photoPath={item?.thumbnail ?? ''}
                            />

                            <div
                              className="position-absolute text-white px-2 py-1 rounded"
                              style={{
                                  bottom: 20,
                                  left: 8,
                                  background: 'rgba(33,33,33,.64)',
                                  fontSize: '0.75rem',
                              }}
                            >
                                {item?.created
                                  ? formatDate(item.created, 'MMMM D, YYYY')
                                  : 'Unknown date'}
                            </div>
                        </div>
                    </a>

                    {/* Stats */}
                    <div className="text-muted px-3">
                        <div className="d-flex justify-content-between">
                            <div>
                                <FaHeart /> {numberFormatter(item?.likes ?? 0)}
                            </div>
                            <div>
                                <FaCommentDots /> {numberFormatter(item?.comments ?? 0)}
                            </div>
                        </div>
                    </div>

                    {/* Text */}
                    <textarea
                      className="form-control mt-2"
                      rows={3}
                      value={item?.text || ''}
                      readOnly
                    />
                </div>
            </Col>
          ))}
      </Row>
    );
};
